import * as React from "react";

import { cn } from "@/lib/utils";

import { createOrganization } from "@/api/organizations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import * as z from "zod";
import FormTemplate, { type FormSectionTemplate } from "../form-template";
import { toast } from "../ui/use-toast";
import { isApiError } from "@/api/_client";

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Required",
  }),
});

interface NewOrganizationFormProps
  extends React.HTMLAttributes<HTMLDivElement> {
  callback?: () => void;
}

export function NewOrganizationForm({
  className,
  callback,
  ...props
}: NewOrganizationFormProps) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await createOrganization(data);
      if (isApiError(res)) {
        toast({
          title: "Error",
          description: "There was an error creating the organization",
        });
      } else {
        toast({
          title: data.name,
          description: "The organization was created",
        });
        queryClient.invalidateQueries({ queryKey: ["organizations"] });
        callback?.();
      }
    },
  });

  type FV = z.infer<typeof formSchema>;
  const form = useForm<FV>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
  });

  const organizationCreateTemplate: FormSectionTemplate<FV>[] = [
    {
      fields: [
        {
          name: "name",
          showTopLabel: true,
          label: "Name",
          placeholder: "Enter your new organization name",
          type: "text",
        },
      ],
    },
  ];

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      {/* {showError && (
        <div className="text-sm text-red-500">
          There was an error resetting your password. This could be because the
          reset link has expired or has already been used. Please try again.
        </div>
      )} */}
      <FormTemplate<FV>
        form={form}
        sections={organizationCreateTemplate}
        onSubmit={mutation.mutateAsync}
        isLoading={mutation.isPending}
        submitLabel="Create organization"
      />
    </div>
  );
}
